import React from 'react'
import PropTypes from 'prop-types'
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Typography, Grid, Box, useTheme, useMediaQuery } from '@mui/material'
import { typography } from 'theme'
import AppSelectComponent from 'components/mui/AppSelectComponent'

// Component scope - Constants and static data
const RADIO_SIZES = ['small', 'medium']

/**
 * App Radio Component - Wrapper for MUI Radio Group
 * @component
 * @param {Object} props - Component props
 * @returns {React.ReactElement} AppRadioComponent
 */
const AppRadioComponent = ({
  size = 'medium',
  label,
  value,
  onChange,
  options = [],
  error = false,
  helperText,
  required = false,
  disabled = false,
  row = false,
  gridLayout = false,
  columnsPerRow = 3,
  boxedMobile = false,
  ...props
}) => {
  const theme = useTheme()
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'))

  // Calculate grid size for equal columns
  const gridSize = gridLayout ? 12 / columnsPerRow : 12

  // Return/rendering
  // On xs screens, render dropdown instead of radio buttons
  if (isXsScreen) {
    return (
      <AppSelectComponent
        label={label}
        value={value}
        onChange={onChange}
        options={options}
        error={error}
        helperText={helperText}
        required={required}
        disabled={disabled}
        placeholder="Select option"
        fullWidth
        InputLabelProps={{ shrink: true }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#2A2D3A',
            color: theme.palette.custom?.text?.body || 'white',
            fontSize: { xs: '16px', sm: 'inherit' },
            height: '56px',
            '& fieldset': {
              borderColor: theme.palette.custom?.stroke?.outlineDisabled || '#3F4252',
            },
            '&:hover fieldset': {
              borderColor: theme.palette.primary.main || '#1455FF',
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main || '#1455FF',
            },
          },
          '& .MuiInputLabel-root': {
            color: theme.palette.custom?.text?.label || 'white',
            fontSize: { xs: '14px', sm: 'inherit' },
          },
          '& .MuiInputLabel-asterisk': {
            color: 'red',
          },
          '& .MuiSelect-icon': {
            color: theme.palette.custom?.text?.body || 'white',
          },
          ...props.sx
        }}
      />
    )
  }

  // For larger screens, render radio buttons
  return (
    <FormControl 
      size={size}
      error={error}
      required={required}
      disabled={disabled}
      {...props}
    >
      {label && (
        <FormLabel
          sx={{
            color: theme.palette.custom?.text?.label || 'white',
            fontSize: { xs: '14px', sm: 'inherit' },
            marginBottom: { xs: '14px', sm: '16px' },
            '& .MuiFormLabel-asterisk': {
              color: 'red',
            }
          }}
        >
          {label}
        </FormLabel>
      )}
      <RadioGroup
        value={value}
        onChange={onChange}
        row={row}
      >
        {boxedMobile ? (
          // Mobile boxed layout
          <Grid container spacing={2}>
            {options.map((option) => {
              const isSelected = value === option.value
              return (
                <Grid item xs={6} key={option.value}>
                  <FormControlLabel
                    value={option.value}
                    control={
                      <Radio 
                        sx={{
                          color: isSelected 
                            ? (theme.palette.primary.main || '#1455FF')
                            : (theme.palette.custom?.stroke?.outlineDisabled || '#3F4252'),
                          '&.Mui-checked': {
                            color: theme.palette.primary.main || '#1455FF'
                          },
                          padding: theme.spacing(0.3)
                        }}
                      />
                    }
                    label={option.label}
                    disabled={disabled}
                    sx={{
                      width: '100%',
                      margin: 0,
                      padding: theme.spacing(1.5, 2),
                      backgroundColor: theme.palette.custom?.background?.popup || '#2A2D3A',
                      border: `1px solid ${isSelected 
                        ? (theme.palette.primary.main || '#1455FF')
                        : (theme.palette.custom?.stroke?.outlineDisabled || '#3F4252')
                      }`,
                      borderRadius: '8px',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: disabled 
                          ? (theme.palette.custom?.stroke?.outlineDisabled || '#3F4252')
                          : (theme.palette.primary.main || '#1455FF'),
                      },
                      '& .MuiFormControlLabel-label': {
                        fontSize: typography.fontSize.paragraph,
                        color: theme.palette.custom?.text?.body || 'white',
                        marginLeft: theme.spacing(1),
                        flex: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                </Grid>
              )
            })}
          </Grid>
        ) : gridLayout ? (
          // Desktop grid layout
          <Grid container spacing={2}>
            {options.map((option) => (
              <Grid item xs={gridSize} key={option.value}>
                <FormControlLabel
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                  disabled={disabled}
                  sx={{
                    width: '100%',
                    margin: 0,
                    whiteSpace: 'nowrap',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px',
                      color: 'white',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '100%'
                    },
                    '& .MuiRadio-root': {
                      color: theme.palette.custom?.stroke?.outlineDisabled || '#3F4252',
                      '&.Mui-checked': {
                        color: theme.palette.primary.main
                      }
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          // Default layout (existing)
          options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={
                <Radio 
                  sx={{
                    color: theme.palette.custom?.stroke?.outlineDisabled || '#3F4252',
                    '&.Mui-checked': {
                      color: theme.palette.primary.main
                    }
                  }}
                />
              }
              label={option.label}
              disabled={disabled}
            />
          ))
        )}
      </RadioGroup>
      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'textSecondary'} sx={{ mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  )
}

AppRadioComponent.propTypes = {
  size: PropTypes.oneOf(RADIO_SIZES),
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired
  })),
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  row: PropTypes.bool,
  gridLayout: PropTypes.bool,
  columnsPerRow: PropTypes.number,
  boxedMobile: PropTypes.bool,
  name: PropTypes.string,
  id: PropTypes.string,
  sx: PropTypes.object
}

AppRadioComponent.defaultProps = {
  size: 'medium',
  options: [],
  error: false,
  required: false,
  disabled: false,
  row: false,
  gridLayout: false,
  columnsPerRow: 3,
  boxedMobile: false
}

export default AppRadioComponent

